import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, StatusDot } from '@/components/atoms';
import { useArticles } from '@/hooks/useArticles';
import type { ArticleSummary } from '@/types/article';

import styles from './Twitter.module.css';

const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'horoscopo', label: 'Horóscopo (25)' },
  { value: 'esportes', label: 'Esportes (13)' },
  { value: 'tecnologia', label: 'Tecnologia (99)' },
] as const;

type FilterKey = (typeof FILTERS)[number]['value'];

const FILTER_CATEGORIES: Record<Exclude<FilterKey, 'all'>, number[]> = {
  horoscopo: [25],
  esportes: [13],
  tecnologia: [99],
};

function isoNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function Twitter() {
  const [filter, setFilter] = useState<FilterKey>('all');

  const params = useMemo(() => {
    const base = {
      after: isoNDaysAgo(7),
      per_page: 50,
      status: 'publish',
    } as const;
    if (filter === 'all') return base;
    return { ...base, categories: FILTER_CATEGORIES[filter] };
  }, [filter]);

  const { data, isLoading, error } = useArticles(params);

  const articles = data?.items ?? [];
  const withTweet = articles.filter((a) => a.twitter_post_id);
  const withoutTweet = articles.filter((a) => !a.twitter_post_id);

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>Twitter / X</h1>
        <Badge tone="info" size="sm">
          últimos 7 dias
        </Badge>
      </header>

      <div className={styles.notice}>
        <strong>Estado actual:</strong> WordPress não está a armazenar o tweet ID como meta nos
        posts. Para popular este monitor, o workflow N8N de Horoscopo Produção precisa de gravar{' '}
        <code>posts.meta.twitter_post_id</code> após a chamada do Twitter API.
      </div>

      <div className={styles.filters} role="group">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            className={`${styles.filterBtn} ${filter === f.value ? styles.filterBtnActive : ''}`}
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{articles.length}</span>
          <span className={styles.metricLabel}>posts</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{withTweet.length}</span>
          <span className={styles.metricLabel}>com tweet</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{withoutTweet.length}</span>
          <span className={styles.metricLabel}>sem tweet</span>
        </div>
      </div>

      {isLoading && <p className={styles.muted}>A carregar…</p>}
      {error && <p className={styles.error}>Falha: {String(error)}</p>}

      <section className={styles.sectionBlock}>
        <h2 className={styles.sectionTitle}>Posts com tweet ({withTweet.length})</h2>
        {withTweet.length === 0 && (
          <p className={styles.muted}>
            Nenhum post desta janela tem tweet_post_id armazenado.
          </p>
        )}
        <ul className={styles.list}>
          {withTweet.map((article) => (
            <TwitterRow key={article.id} article={article} hasTweet />
          ))}
        </ul>
      </section>

      <section className={styles.sectionBlock}>
        <h2 className={styles.sectionTitle}>
          Posts publicados (sem tweet_post_id) — amostra de{' '}
          {Math.min(withoutTweet.length, 10)}
        </h2>
        <ul className={styles.list}>
          {withoutTweet.slice(0, 10).map((article) => (
            <TwitterRow key={article.id} article={article} hasTweet={false} />
          ))}
        </ul>
      </section>
    </section>
  );
}

interface RowProps {
  article: ArticleSummary;
  hasTweet: boolean;
}

function TwitterRow({ article, hasTweet }: RowProps) {
  return (
    <li className={styles.row}>
      <StatusDot state={hasTweet ? 'ok' : 'off'} />
      <div className={styles.rowBody}>
        <Link to={`/artigos/${article.id}`} className={styles.rowLink}>
          {article.title}
        </Link>
        <div className={styles.rowMeta}>
          <span>{new Date(article.published_at).toLocaleString('pt-BR')}</span>
          {hasTweet && article.twitter_post_id && (
            <a
              href={`https://x.com/i/status/${article.twitter_post_id}`}
              target="_blank"
              rel="noreferrer"
              className={styles.tweetLink}
            >
              Ver tweet ↗
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
